import React, { useState, useEffect } from 'react';
import apiService from '../services/apiService';
import Modal from '../components/common/Modal';
import DeceasedDetailView from '../components/deceased/DeceasedDetailView';
import { Skull, Search } from 'lucide-react';

const fmt = (v) => new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(v ?? 0);

const DeceasedPage = ({ formatCurrency: fmtProp }) => {
  const formatCurrency = fmtProp || fmt;
  const [deceasedRecords, setDeceasedRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);

  useEffect(() => { fetchDeceasedRecords(); }, []); // eslint-disable-line

  const fetchDeceasedRecords = async () => {
    try { const data = await apiService.getDeceasedRecords(); setDeceasedRecords(data); }
    catch (e) { console.error('Error fetching deceased records:', e); }
    finally { setLoading(false); }
  };

  const filteredRecords = deceasedRecords.filter(r =>
    r.deceased_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    r.parent_member_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    r.membership_ref?.toString().includes(searchTerm));

  const relationLabel = (r) => r === 'S' ? 'Son' : r === 'D' ? 'Daughter' : r === 'W' ? 'Wife' : r === 'H' ? 'Husband' : r;

  if (loading) {
    return (
      <div className="p-3 sm:p-6">
        <div className="bg-white rounded-xl shadow-lg p-12 text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-slate-400 text-sm">Loading deceased records…</p>
        </div>
      </div>
    );
  }

  const thisYear = new Date().getFullYear();
  const ytdRecords = deceasedRecords.filter(r => new Date(r.date_of_death).getFullYear() === thisYear);

  return (
    <div className="p-3 sm:p-6 space-y-6">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-1">Deceased Records</h1>
        <p className="text-slate-600 text-sm">Bereavement claims and family member records</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-5">
          <p className="text-sm text-slate-500 mb-1">This Year</p>
          <p className="text-2xl font-bold text-red-600">{ytdRecords.length}</p>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-5">
          <p className="text-sm text-slate-500 mb-1">Primary Members</p>
          <p className="text-2xl font-bold text-orange-600">{deceasedRecords.filter(r => r.was_primary_member).length}</p>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-5">
          <p className="text-sm text-slate-500 mb-1">Family Members</p>
          <p className="text-2xl font-bold text-purple-600">{deceasedRecords.filter(r => !r.was_primary_member).length}</p>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-5">
          <p className="text-sm text-slate-500 mb-1">Total Costs YTD</p>
          <p className="text-2xl font-bold text-blue-600">{formatCurrency(ytdRecords.reduce((sum, r) => sum + parseFloat(r.cost || 0), 0))}</p>
        </div>
      </div>

      {/* Table card */}
      <div className="bg-white rounded-xl shadow-lg overflow-hidden">
        <div className="bg-gradient-to-r from-slate-700 to-slate-800 text-white px-4 sm:px-6 py-4">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <Skull className="w-5 h-5" />
              <span className="font-semibold">Records ({filteredRecords.length})</span>
            </div>
            <div className="relative w-full sm:w-64">
              <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-white/70" />
              <input type="text" placeholder="Search by name or ref…" value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                className="w-full pl-8 pr-3 py-1.5 bg-white/20 border border-white/30 text-white placeholder-white/60 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-white/50" />
            </div>
          </div>
        </div>

        <div className="overflow-x-auto">
          {filteredRecords.length === 0 ? (
            <div className="text-center py-16 text-slate-400 text-sm">
              {searchTerm ? 'No records match your search' : 'No deceased records found'}
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  <th className="text-left py-3 px-4 text-xs font-medium text-slate-500 uppercase tracking-wider">Deceased</th>
                  <th className="text-left py-3 px-4 text-xs font-medium text-slate-500 uppercase tracking-wider">Type</th>
                  <th className="text-left py-3 px-4 text-xs font-medium text-slate-500 uppercase tracking-wider hidden md:table-cell">Family</th>
                  <th className="text-left py-3 px-4 text-xs font-medium text-slate-500 uppercase tracking-wider">Date of Death</th>
                  <th className="text-left py-3 px-4 text-xs font-medium text-slate-500 uppercase tracking-wider hidden md:table-cell">Age</th>
                  <th className="text-left py-3 px-4 text-xs font-medium text-slate-500 uppercase tracking-wider">Cost</th>
                  <th className="text-left py-3 px-4 text-xs font-medium text-slate-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredRecords.map(record => (
                  <tr key={record.id} className="hover:bg-slate-50 transition-colors">
                    <td className="py-3 px-4">
                      <p className="font-medium text-slate-800">{record.deceased_name}</p>
                      {record.membership_ref && <p className="text-xs text-slate-400">#{record.membership_ref}</p>}
                    </td>
                    <td className="py-3 px-4">
                      <span className={`px-2 py-0.5 text-xs font-medium rounded-full
                        ${record.was_primary_member ? 'bg-red-100 text-red-700' : 'bg-purple-100 text-purple-700'}`}>
                        {record.was_primary_member ? 'Primary' : 'Family'}
                      </span>
                    </td>
                    <td className="py-3 px-4 hidden md:table-cell">
                      <p className="text-slate-700">{record.parent_member_name || 'N/A'}</p>
                      {record.relation && <p className="text-xs text-slate-400">{relationLabel(record.relation)}</p>}
                    </td>
                    <td className="py-3 px-4 text-slate-600">
                      {new Date(record.date_of_death).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                    </td>
                    <td className="py-3 px-4 text-slate-500 hidden md:table-cell">
                      {record.age_at_death ? `${record.age_at_death} yrs` : '—'}
                    </td>
                    <td className="py-3 px-4 font-semibold text-green-600">{formatCurrency(record.cost || 0)}</td>
                    <td className="py-3 px-4">
                      <button onClick={() => { setSelectedRecord(record); setIsDetailModalOpen(true); }}
                        className="px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white text-xs rounded-lg font-medium transition-colors">
                        Details
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {isDetailModalOpen && selectedRecord && (
        <Modal onClose={() => setIsDetailModalOpen(false)}>
          <DeceasedDetailView record={selectedRecord} formatCurrency={formatCurrency} onClose={() => setIsDetailModalOpen(false)} />
        </Modal>
      )}
    </div>
  );
};

export default DeceasedPage;
